import React, {useCallback, useEffect, useState} from 'react';
import moment from 'moment';
import {toast} from 'react-toastify';
import {CalendarDaysIcon, TrashIcon} from '@heroicons/react/24/outline';

import usePageTitle from '../../../hooks/usePageTitle';
import useSelectedResource from '../../../hooks/useSelectedResource';
import {useBreadcrumbs} from '../../../contexts/BreadcrumbContext';
import {axiosInstance} from '../../../routes/axiosInstance';
import {handleThunkError} from '../../../slices/userSlice';
import Card, {CardHeader} from '../../../components/ui/Card';
import ConfirmDialog from '../../../components/ConfirmDialog';
import {daysBetween, formatHorizon} from '../../../utils/excludedDays';
import {getLocalTimezoneLabel} from '../../../utils/datetime';

// The API accepts at most this many days per request.
const MAX_DAYS_PER_REQUEST = 31;

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 '
  + 'focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900';

const inputClass = 'rounded-md border border-gray-300 dark:border-gray-600 '
  + 'bg-surface dark:bg-gray-870 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 '
  + 'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

const checkboxClass = 'rounded border-gray-300 dark:border-gray-600 text-brand-600 '
  + 'focus:ring-brand-500';

const buttonClass = 'inline-flex items-center px-3 py-1.5 rounded-md border '
  + 'border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-xs '
  + 'font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 '
  + 'dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed '
  + `transition-colors ${focusRing}`;

const formatDay = (isoDay) => moment(isoDay, 'YYYY-MM-DD').format('D MMM YYYY');

const pluralize = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Operations page where superusers flag outage days of the selected market.
 * An excluded (target day, horizon) is left out of rankings, the leaderboard
 * error and payments; its daily scores are kept, so lifting it needs no
 * rescoring.
 */
export default function ExcludedDays() {
  usePageTitle('Excluded days');
  useBreadcrumbs(
    [{label: 'Operations', href: '/operations'}, {label: 'Excluded days'}],
    'Leave outage days out of rankings and payments');

  const resource = useSelectedResource();
  const resourceId = resource?.id;
  // null while loading.
  const [exclusions, setExclusions] = useState(null);
  const [horizons, setHorizons] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  // Bumped after a change to fetch the list again.
  const [listVersion, setListVersion] = useState(0);
  const reloadExclusions = useCallback(() => setListVersion((v) => v + 1), []);

  // Each effect ignores answers that arrive after the market (or filter)
  // changed, so a slow request cannot show another market's days.
  useEffect(() => {
    setExclusions(null);
    if (!resourceId) return undefined;
    let stale = false;
    const query = showRemoved ? '&include_removed=true' : '';
    axiosInstance.get(`/market/excluded-days/?resource=${resourceId}${query}`)
      .then((response) => {
        if (!stale) setExclusions(response.data.data || []);
      })
      .catch((error) => {
        if (stale) return;
        setExclusions([]);
        toast.error(handleThunkError(error, 'Could not load the excluded days.'));
      });
    return () => {
      stale = true;
    };
  }, [resourceId, showRemoved, listVersion]);

  useEffect(() => {
    setHorizons(null);
    if (!resourceId) return undefined;
    let stale = false;
    axiosInstance.get(`/data/available-horizons/?resource=${resourceId}`)
      .then((response) => {
        if (!stale) setHorizons(response.data.data || []);
      })
      .catch((error) => {
        if (stale) return;
        setHorizons([]);
        toast.error(handleThunkError(error, 'Could not load the market\'s horizons.'));
      });
    return () => {
      stale = true;
    };
  }, [resourceId]);

  const removeExclusion = (exclusion) => {
    setPendingRemoval(null);
    axiosInstance.delete(`/market/excluded-days/${exclusion.id}`)
      .then(() => {
        toast.success('Exclusion removed.');
        reloadExclusions();
      })
      .catch((error) => toast.error(
        handleThunkError(error, 'Could not remove the exclusion.')));
  };

  if (!resource) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center">
        <CalendarDaysIcon className="h-16 w-16 text-gray-400 dark:text-gray-600 mb-4"/>
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          No Resource Selected
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Select a resource from the dropdown above to manage its excluded days.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Remove exclusion"
        description={pendingRemoval
          ? `${formatDay(pendingRemoval.target_day)} (${formatHorizon(pendingRemoval.horizon_offset)}) `
            + 'will count again in rankings and payments.'
          : ''}
        confirmText="Remove exclusion"
        onConfirm={() => removeExclusion(pendingRemoval)}
        onCancel={() => setPendingRemoval(null)}
      />

      <Card>
        <CardHeader
          title={`Flag outage days for ${resource.name}`}
          description="Each day is excluded on the horizons you tick."
        />
        <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
          <li>Excluded days are left out of rankings, the leaderboard error and payments.</li>
          <li>Daily scores are kept, so an exclusion can be removed later.</li>
          <li>
            A month can no longer change once its payment emails are sent, or once it is
            older than last month and has calculated payments.
          </li>
          <li>
            A closed month&apos;s Ranking and Errors tabs are rebuilt within the hour. If last
            month&apos;s payments were already calculated, calculate them again on the
            Payments page.
          </li>
          <li>Forecasters can see the reason.</li>
        </ul>
        {/* key: a market switch starts from an empty form. */}
        <ExcludeDaysForm
          key={resourceId}
          resourceId={resourceId}
          horizons={horizons}
          onExcluded={reloadExclusions}
        />
      </Card>

      <Card>
        <CardHeader
          title="Excluded days"
          actions={(
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={showRemoved}
                className={checkboxClass}
                onChange={(e) => setShowRemoved(e.target.checked)}
              />
              Show removed
            </label>
          )}
        />
        <ExclusionsTable exclusions={exclusions} onRemove={setPendingRemoval}/>
      </Card>
    </div>
  );
}

function ExcludeDaysForm({resourceId, horizons, onExcluded}) {
  const [firstDay, setFirstDay] = useState('');
  const [lastDay, setLastDay] = useState('');
  const [checkedHorizons, setCheckedHorizons] = useState([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const targetDays = daysBetween(firstDay, lastDay);
  const tooManyDays = targetDays.length > MAX_DAYS_PER_REQUEST;
  const canSubmit = targetDays.length > 0 && !tooManyDays
    && checkedHorizons.length > 0 && reason.trim() !== '' && !submitting;

  const toggleHorizon = (offset) => setCheckedHorizons((current) => (
    current.includes(offset)
      ? current.filter((h) => h !== offset)
      : [...current, offset].sort((a, b) => a - b)));

  const submit = () => {
    setSubmitting(true);
    axiosInstance.post('/market/excluded-days/', {
      resource: resourceId,
      target_days: targetDays,
      horizon_offsets: checkedHorizons,
      reason: reason.trim(),
    })
      .then((response) => {
        const {created, skipped} = response.data.data;
        toast.success(skipped.length
          ? `${pluralize(created.length, 'exclusion')} added; `
            + `${skipped.length} already existed.`
          : `${pluralize(created.length, 'exclusion')} added.`);
        onExcluded();
      })
      .catch((error) => toast.error(
        handleThunkError(error, 'Could not exclude the days.')))
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="mt-4 flex flex-wrap items-end gap-4">
      <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
        First day
        <input type="date" value={firstDay} className={inputClass}
          onChange={(e) => setFirstDay(e.target.value)}/>
      </label>
      <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
        Last day
        <input type="date" value={lastDay} className={inputClass}
          onChange={(e) => setLastDay(e.target.value)}/>
      </label>
      <fieldset className="flex flex-col gap-1">
        <legend className="text-xs text-gray-600 dark:text-gray-300">Horizons</legend>
        <div className="flex flex-wrap gap-3 py-1">
          {horizons === null && (
            <span className="text-xs text-gray-500 dark:text-gray-400">Loading horizons...</span>
          )}
          {horizons?.length === 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              No horizons configured for this market.
            </span>
          )}
          {(horizons || []).map((offset) => (
            <label key={offset}
              className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                className={checkboxClass}
                checked={checkedHorizons.includes(offset)}
                onChange={() => toggleHorizon(offset)}
              />
              {formatHorizon(offset)}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300 grow max-w-md">
        Reason
        <input type="text" value={reason} maxLength={255} className={inputClass}
          placeholder="e.g. Platform outage, submissions rejected"
          onChange={(e) => setReason(e.target.value)}/>
      </label>
      <div className="flex items-center gap-2">
        <button type="button" className={buttonClass} disabled={!canSubmit} onClick={submit}>
          Exclude days
        </button>
        {targetDays.length > 0 && (
          <span className={`text-xs ${tooManyDays
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-500 dark:text-gray-400'}`}>
            {pluralize(targetDays.length, 'day')}
            {tooManyDays ? ` (at most ${MAX_DAYS_PER_REQUEST} per request)` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

function ExclusionsTable({exclusions, onRemove}) {
  if (exclusions === null) {
    return (
      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading excluded days...</p>
    );
  }
  if (exclusions.length === 0) {
    return (
      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
        No excluded days for this market.
      </p>
    );
  }
  return (
    <table className="mt-3 w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200/70 dark:border-gray-600/70">
          <th className="py-2 pr-3 font-semibold">Target day</th>
          <th className="py-2 pr-3 font-semibold">Horizon</th>
          <th className="py-2 pr-3 font-semibold">Reason</th>
          <th className="py-2 pr-3 font-semibold">Flagged by</th>
          <th className="py-2 pr-3 font-semibold"
            title="Whether the month's Ranking and Errors tabs were rebuilt with this change (hourly)">
            Monthly stats
          </th>
          <th className="py-2"><span className="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {exclusions.map((exclusion) => {
          const removed = Boolean(exclusion.removed_at);
          return (
            <tr key={exclusion.id} className={removed ? 'opacity-60' : undefined}>
              <td className="py-2 pr-3 font-mono text-xs text-gray-900 dark:text-gray-100 whitespace-nowrap">
                {formatDay(exclusion.target_day)}
              </td>
              <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                {formatHorizon(exclusion.horizon_offset)}
              </td>
              <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{exclusion.reason}</td>
              <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">
                {exclusion.created_by_email || '-'}
                <div>
                  {moment(exclusion.created_at).format('D MMM YYYY HH:mm')}{' '}
                  {getLocalTimezoneLabel(exclusion.created_at)}
                </div>
              </td>
              <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {exclusion.leaderboard_updated ? 'Rebuilt' : 'Rebuild pending'}
              </td>
              <td className="py-2 text-right text-xs whitespace-nowrap">
                {removed ? (
                  <span className="text-gray-500 dark:text-gray-400">
                    Removed by {exclusion.removed_by_email || '-'}
                    {' on '}{moment(exclusion.removed_at).format('D MMM YYYY')}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onRemove(exclusion)}
                    className={`inline-flex items-center gap-1 rounded font-semibold text-accent-600 dark:text-accent-400 hover:underline ${focusRing}`}
                  >
                    <TrashIcon className="h-4 w-4"/>
                    Remove
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
